import { Link } from 'react-router-dom';
import { useEffect } from 'react';
import Navbar from '../../../../components/Navbar';
import Footer from '../../../home/components/Footer';

export default function PATTestingPage() {
  useEffect(() => {
    document.title = 'PAT Testing Watford | Portable Appliance Testing | Beyond Electrical';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Professional PAT testing in Watford. Portable appliance testing for landlords, businesses & offices. Certified reports & labels. Call 020 3198 1818 for a quote.');
    } else {
      const meta = document.createElement('meta');
      meta.name = 'description';
      meta.content = 'Professional PAT testing in Watford. Portable appliance testing for landlords, businesses & offices. Certified reports & labels. Call 020 3198 1818 for a quote.';
      document.head.appendChild(meta);
    }

    const schema = {
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      "name": "Beyond Electrical",
      "image": "https://beyondelectrical.co.uk/logo.png",
      "url": "https://beyondelectrical.co.uk/services/repairs/pat-testing",
      "telephone": "02031981818",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "Watford",
        "addressLocality": "Watford",
        "addressRegion": "Hertfordshire",
        "postalCode": "WD17",
        "addressCountry": "GB"
      },
      "geo": {
        "@type": "GeoCoordinates",
        "latitude": 51.6565,
        "longitude": -0.3957
      },
      "openingHoursSpecification": {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": [
          "Monday",
          "Tuesday",
          "Wednesday",
          "Thursday",
          "Friday",
          "Saturday",
          "Sunday"
        ],
        "opens": "00:00",
        "closes": "23:59"
      },
      "serviceType": "PAT Testing",
      "areaServed": {
        "@type": "City",
        "name": "Watford"
      }
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(schema);
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  const services = [
    {
      emoji: '🔌',
      name: 'Visual Inspections',
      description: 'Thorough visual examination of electrical appliances for damage, wear, or modifications that could pose safety risks.'
    },
    {
      emoji: '⚡',
      name: 'Earth Continuity Testing',
      description: 'Testing the earth connection to ensure proper grounding and protection against electric shock in case of faults.'
    },
    {
      emoji: '🔋',
      name: 'Insulation Resistance',
      description: 'Measuring insulation resistance to detect deterioration that could lead to electric shock or fire hazards.'
    },
    {
      emoji: '📊',
      name: 'Load Testing',
      description: 'Testing appliances under normal operating conditions to ensure they function safely and efficiently.'
    },
    {
      emoji: '🏷️',
      name: 'PAT Test Labels',
      description: 'Durable pass/fail labels applied to all tested appliances showing test date and next test due date.'
    },
    {
      emoji: '📋',
      name: 'Detailed Reports',
      description: 'Comprehensive test reports documenting all appliances tested, results, and recommendations for compliance records.'
    }
  ];

  const whoNeeds = [
    {
      emoji: '🏢',
      sector: 'Businesses &amp; Offices',
      description: 'Legal requirement for employers to ensure all portable electrical equipment is safe for employee use.'
    },
    {
      emoji: '🏨',
      sector: 'Hotels &amp; Hospitality',
      description: 'Essential for guest safety and insurance compliance in hotels, B&amp;Bs, and holiday rentals.'
    },
    {
      emoji: '🏫',
      sector: 'Schools &amp; Colleges',
      description: 'Mandatory testing to protect students and staff from electrical hazards in educational settings.'
    },
    {
      emoji: '🔑',
      sector: 'Landlords',
      description: 'Required for rental properties to ensure tenant safety and meet insurance requirements.'
    },
    {
      emoji: '🏥',
      sector: 'Healthcare Facilities',
      description: 'Critical for patient and staff safety in medical practices, care homes, and health centers.'
    },
    {
      emoji: '🏪',
      sector: 'Retail &amp; Shops',
      description: 'Necessary for customer and staff safety, plus compliance with health and safety regulations.'
    }
  ];

  const testingProcess = [
    {
      step: '1',
      title: 'Schedule Your Test',
      description: 'Contact us to arrange a convenient time for PAT testing at your Watford premises with minimal disruption.'
    },
    {
      step: '2',
      title: 'Visual Inspection',
      description: 'Our qualified technician examines each appliance for visible damage, wear, or safety concerns.'
    },
    {
      step: '3',
      title: 'Electrical Testing',
      description: 'Comprehensive electrical tests using calibrated equipment to verify safety and compliance.'
    },
    {
      step: '4',
      title: 'Labeling',
      description: 'Pass/fail labels applied to all tested appliances with test date and next test due date.'
    },
    {
      step: '5',
      title: 'Documentation',
      description: 'Detailed test report provided documenting all appliances, results, and any recommendations.'
    },
    {
      step: '6',
      title: 'Ongoing Support',
      description: 'We can schedule regular testing intervals to ensure continuous compliance and safety.'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero Section */}
      <section className="relative min-h-[70vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#0A1628] via-[#0F2847] to-[#0A1628]"></div>
        <div className="absolute inset-0 bg-[url('https://public.readdy.ai/ai/img_res/8dc353af95ff9bb72d62368e3f2043d3.jpg')] bg-cover bg-center opacity-10"></div>
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40"></div>
        
        <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center">
          <div className="flex flex-wrap items-center justify-center gap-3 mb-6">
            <div className="flex items-center gap-2 bg-[#FFD700]/20 border border-[#FFD700] text-[#FFD700] px-4 py-2 rounded-full text-sm font-semibold">
              <i className="ri-shield-check-fill"></i>
              Qualified Technicians
            </div>
            <div className="flex items-center gap-2 bg-[#FFD700]/20 border border-[#FFD700] text-[#FFD700] px-4 py-2 rounded-full text-sm font-semibold">
              <i className="ri-map-pin-fill"></i>
              Serving Watford
            </div>
            <div className="flex items-center gap-2 bg-[#FFD700]/20 border border-[#FFD700] text-[#FFD700] px-4 py-2 rounded-full text-sm font-semibold">
              <i className="ri-file-list-3-fill"></i>
              Full Certification
            </div>
          </div>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
            PAT Testing Services<br />
            <span className="text-[#FFD700]">in Watford</span>
          </h1>

          <p className="text-lg sm:text-xl text-gray-200 mb-8 max-w-3xl mx-auto leading-relaxed">
            Professional Portable Appliance Testing (PAT) services in Watford, Rickmansworth, and surrounding areas. Ensure your electrical equipment is safe, compliant, and meets insurance requirements with our comprehensive PAT testing service.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href="tel:02031981818"
              className="w-full sm:w-auto bg-[#FFD700] text-[#0A1628] px-8 py-4 rounded-full font-bold text-lg hover:bg-[#FFC700] transition-all duration-300 shadow-xl shadow-[#FFD700]/30 flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-phone-fill"></i>
              020 3198 1818
            </a>
            <a
              href="/#contact"
              className="w-full sm:w-auto bg-white/10 backdrop-blur-sm border-2 border-white text-white px-8 py-4 rounded-full font-bold text-lg hover:bg-white hover:text-[#0A1628] transition-all duration-300 flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-mail-fill"></i>
              Get a Quote
            </a>
          </div>
        </div>
      </section>

      {/* What's Included */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-white to-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0A1628] mb-4">
              Our PAT Testing Service
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Comprehensive portable appliance testing to ensure electrical safety and compliance
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-16">
            {services.map((service, index) => (
              <div
                key={index}
                className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-100"
              >
                <div className="w-14 h-14 flex items-center justify-center bg-gradient-to-br from-[#FFD700] to-[#FFC700] rounded-xl text-3xl mb-4">
                  {service.emoji}
                </div>
                <h3 className="text-xl font-bold text-[#0A1628] mb-3">
                  {service.name}
                </h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  {service.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Who Needs PAT Testing */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0A1628] mb-4">
              Who Needs PAT Testing?
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              PAT testing is essential for various sectors to ensure electrical safety and compliance
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {whoNeeds.map((item, index) => (
              <div
                key={index}
                className="bg-gradient-to-br from-gray-50 to-white rounded-2xl p-6 border border-gray-200"
              >
                <div className="w-12 h-12 flex items-center justify-center bg-[#FFD700] rounded-lg text-2xl mb-4">
                  {item.emoji}
                </div>
                <h3 className="text-lg font-bold text-[#0A1628] mb-2">
                  {item.sector}
                </h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  {item.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testing Process */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0A1628] mb-4">
              Our PAT Testing Process
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Simple, efficient PAT testing with minimal disruption to your business
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {testingProcess.map((item, index) => (
              <div
                key={index}
                className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100"
              >
                <div className="w-12 h-12 flex items-center justify-center bg-[#FFD700] rounded-full text-xl font-bold text-[#0A1628] mb-4">
                  {item.step}
                </div>
                <h3 className="text-lg font-bold text-[#0A1628] mb-2">
                  {item.title}
                </h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  {item.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-[#0A1628] to-[#0F2847]">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">
              Why Choose Beyond Electrical?
            </h2>
            <p className="text-lg text-gray-300 max-w-2xl mx-auto">
              Professional PAT testing services you can trust in Watford
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
            <div className="bg-white/5 backdrop-blur-sm border border-[#FFD700]/30 rounded-2xl p-8 text-center">
              <div className="w-16 h-16 flex items-center justify-center bg-[#FFD700] rounded-full text-3xl mx-auto mb-4">
                ✅
              </div>
              <h3 className="text-2xl font-bold text-white mb-3">Qualified Technicians</h3>
              <p className="text-gray-300 leading-relaxed">
                All PAT testing carried out by qualified technicians using calibrated equipment
              </p>
            </div>

            <div className="bg-white/5 backdrop-blur-sm border border-[#FFD700]/30 rounded-2xl p-8 text-center">
              <div className="w-16 h-16 flex items-center justify-center bg-[#FFD700] rounded-full text-3xl mx-auto mb-4">
                📋
              </div>
              <h3 className="text-2xl font-bold text-white mb-3">Full Documentation</h3>
              <p className="text-gray-300 leading-relaxed">
                Comprehensive test reports and certification for compliance and insurance purposes
              </p>
            </div>

            <div className="bg-white/5 backdrop-blur-sm border border-[#FFD700]/30 rounded-2xl p-8 text-center">
              <div className="w-16 h-16 flex items-center justify-center bg-[#FFD700] rounded-full text-3xl mx-auto mb-4">
                ⚡
              </div>
              <h3 className="text-2xl font-bold text-white mb-3">Fast &amp; Efficient</h3>
              <p className="text-gray-300 leading-relaxed">
                Quick testing with minimal disruption to your business operations
              </p>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <div className="text-center">
              <div className="text-4xl font-bold text-[#FFD700] mb-2">5000+</div>
              <div className="text-gray-300 text-sm">Appliances Tested</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-[#FFD700] mb-2">100%</div>
              <div className="text-gray-300 text-sm">Compliance Rate</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-[#FFD700] mb-2">200+</div>
              <div className="text-gray-300 text-sm">Business Clients</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-[#FFD700] mb-2">15+</div>
              <div className="text-gray-300 text-sm">Years Experience</div>
            </div>
          </div>
        </div>
      </section>

      {/* Areas Covered */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-6xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-[#0A1628] mb-6">
            Areas We Cover
          </h2>
          <p className="text-lg text-gray-600 mb-8 max-w-3xl mx-auto">
            Professional PAT testing services throughout Watford, Rickmansworth, and surrounding areas including:
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3">
            {['Watford', 'Rickmansworth', 'Bushey', 'Croxley Green', 'Abbots Langley', 'Kings Langley', 'Chorleywood', 'Northwood', 'Pinner', 'Harrow', 'Borehamwood', 'Radlett'].map((area) => (
              <span
                key={area}
                className="bg-gradient-to-r from-[#FFD700] to-[#FFC700] text-[#0A1628] px-5 py-2.5 rounded-full font-semibold text-sm shadow-md"
              >
                {area}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-[#0A1628] via-[#0F2847] to-[#0A1628]">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6">
            Book Your PAT Testing Today
          </h2>
          <p className="text-lg sm:text-xl text-gray-300 mb-10 leading-relaxed">
            Contact us to schedule PAT testing for your Watford business or property. Ensure electrical safety and compliance with our professional testing service.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href="tel:02031981818"
              className="w-full sm:w-auto bg-[#FFD700] text-[#0A1628] px-10 py-5 rounded-full font-bold text-lg hover:bg-[#FFC700] transition-all duration-300 shadow-2xl shadow-[#FFD700]/40 flex items-center justify-center gap-3 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-phone-fill text-xl"></i>
              Call 020 3198 1818
            </a>
            <a
              href="/#contact"
              className="w-full sm:w-auto bg-white/10 backdrop-blur-sm border-2 border-white text-white px-10 py-5 rounded-full font-bold text-lg hover:bg-white hover:text-[#0A1628] transition-all duration-300 flex items-center justify-center gap-3 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-mail-fill text-xl"></i>
              Request a Quote
            </a>
          </div>
          <div className="mt-8 flex items-center justify-center gap-2 text-[#FFD700] text-sm font-semibold">
            <i className="ri-shield-check-fill"></i>
            Full Certification Provided
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}